import { useCallback, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Admin from "@/pages/Admin";
import Classic from "@/pages/Classic";
import { ContentProvider } from "@/content/ContentContext";
import "@/App.css";
import Scene from "@/components/room/Scene";
import { Intro } from "@/components/hud/Intro";
import { TopBar, NavDock, Hint } from "@/components/hud/Hud";
import { Panel } from "@/components/hud/Panel";
import { Lightbox } from "@/components/hud/Lightbox";
import { track } from "@/lib/track";

const VisitTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname === "/" || pathname === "/cv") track("visit");
    if (pathname === "/cv") track("classic");
  }, [pathname]);
  return null;
};

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
};

function Room() {
  const isMobile = useIsMobile();
  const [entered, setEntered] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [showHotspots, setShowHotspots] = useState(true);
  const [resetKey, setResetKey] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [visited, setVisited] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("visited") || "[]"); } catch { return []; }
  });

  const onSelect = useCallback((id) => {
    setActiveId(id);
    setVisited((v) => {
      if (!id || v.includes(id)) return v;
      const next = [...v, id];
      try { sessionStorage.setItem("visited", JSON.stringify(next)); } catch { /* stockage indisponible */ }
      return next;
    });
  }, []);
  const onClose = useCallback(() => setActiveId(null), []);
  const onReset = () => { setActiveId(null); setResetKey((k) => k + 1); };
  const openLightbox = (items, index) => setLightbox({ items, index });
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const moveLightbox = useCallback((d) => setLightbox((s) => s && { ...s, index: (s.index + d + s.items.length) % s.items.length }), []);

  const ui = { entered, activeId, onSelect, showHotspots, visited };

  return (
    <div className="room-app fixed inset-0 overflow-hidden bg-[#12100E]">
      <div className="absolute inset-0">
        <Scene ui={ui} isMobile={isMobile} resetKey={resetKey} />
      </div>
      <div className="vignette pointer-events-none fixed inset-0 z-10" />
      <div className="grain pointer-events-none fixed inset-0 z-[80]" />
      {entered && (
        <>
          <TopBar onReset={onReset} onSelect={onSelect} visited={visited} showHotspots={showHotspots} toggleHotspots={() => setShowHotspots((v) => !v)} />
          <NavDock activeId={activeId} onSelect={onSelect} hidden={isMobile && !!activeId} shifted={!isMobile && !!activeId} />
          {!activeId && <Hint />}
        </>
      )}
      <Panel activeId={activeId} isMobile={isMobile} onClose={onClose} onSelect={onSelect} openLightbox={openLightbox} />
      <Lightbox state={lightbox} onClose={closeLightbox} onMove={moveLightbox} />
      <AnimatePresence>{!entered && <Intro key="intro" onEnter={() => { setEntered(true); track("room"); }} />}</AnimatePresence>
    </div>
  );
}

function App() {
  return (
    <ContentProvider>
    <BrowserRouter>
      <VisitTracker />
      <Routes>
        <Route path="/" element={<Room />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/cv" element={<Classic />} />
      </Routes>
      <Toaster position="top-center" theme="dark" richColors />
    </BrowserRouter>
    </ContentProvider>
  );
}

export default App;
